import { useEffect, useState, useRef } from 'react';
import Flow from '@flowjs/flow.js';
import { ENV } from './env';
import { useSelector } from 'react-redux';
import type { FlowFile } from 'flowjs';
import { getJwtToken } from '@features/auth/auth.slice';
import { CdnFile } from '@services/core/cdn/file/file';

export type FilesComponentsProps = {
    onSuccess?: (file: CdnFile, flowFile: FlowFile) => void;
    onError?: (file: FlowFile) => void;
    onAdded?: (file: FlowFile) => void;
};

type FlowEventCallback = (...args: unknown[]) => void;

export const useFlowTs = ({ onSuccess, onError, onAdded }: FilesComponentsProps) => {
    const jwt = useSelector(getJwtToken);
    const [flow, setFlow] = useState<Flow | null>(null);
    const flowRef = useRef<Flow | null>(null);
    const errorFilesRef = useRef<Set<string>>(new Set());

    useEffect(() => {
        if (!jwt) return;

        const options = {
            target: `${ENV.API_URL}/cdn/file/import?jwt=${jwt}`,
            chunkSize: 1024 * 1024 * 10,
            permanentErrors: [400, 401, 404, 500],
            maxChunkRetries: 5,
            chunkRetryInterval: 1000,
            allowDuplicateUploads: true
        };

        const flowInstance = new Flow(options);

        flowRef.current = flowInstance;
        setFlow(flowInstance);

        const handleFileAdded: FlowEventCallback = (flowFile) => {
            errorFilesRef.current.clear();

            if (onAdded) onAdded(flowFile as FlowFile);
            setTimeout(() => {
                (flowFile as FlowFile).flowObj.upload();
            }, 0);
        };

        const handleFileSuccess: FlowEventCallback = (_file, apiFile) => {
            if (onSuccess) onSuccess(JSON.parse(apiFile as string).data, _file as FlowFile);
        };

        const handleFileError: FlowEventCallback = (file) => {
            const fileId = (file as FlowFile).uniqueIdentifier;
            if (!errorFilesRef.current.has(fileId)) {
                errorFilesRef.current.add(fileId);
                if (onError) onError(file as FlowFile);
            }
        };

        flowInstance.on('fileAdded', handleFileAdded);
        flowInstance.on('fileSuccess', handleFileSuccess);
        flowInstance.on('fileError', handleFileError);

        return () => {
            if (flowRef.current) {
                flowRef.current.off('fileAdded');
                flowRef.current.off('fileSuccess');
                flowRef.current.off('fileError');
            }
            errorFilesRef.current.clear();
        };
    }, [jwt, onSuccess, onError, onAdded]);

    return {
        flow
    };
};
